package org.example.project.enginevertrieb

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.contentOrNull
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.RequestInit

internal object LinkedInSyncState {
    var version by mutableStateOf(0)
    var busy by mutableStateOf(false)
    var status by mutableStateOf(All())
}

internal object LinkedInFlowApi {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private suspend fun read(action: String, id: String, post: Boolean = false): String {
        val response = window.fetch("${LumaticaApiSettings.getBaseUrl()}/api/linkedinflow/$action/$id", RequestInit(method = if (post) "POST" else "GET", headers = LumaticaApiManager.auditHeaders())).await()
        val raw = response.text().await()
        if (!response.ok) error(runCatching { json.parseToJsonElement(raw).jsonObject["message"]?.jsonPrimitive?.contentOrNull }.getOrNull()
            ?: "LinkedIn-Daten konnten nicht geladen werden (HTTP ${response.status}).")
        return raw
    }
    suspend fun messages(id: String): List<All> = json.decodeFromString(ListSerializer(All.serializer()), read("messages", id))
        .map { message ->
            // Preserve native event IDs. Older API responses used a new row number for
            // every copy; identify those by their complete, unchanged message fields.
            val identity = if (message.id.startsWith("linkedin-message-")) message.id else
                listOf(message.linkedinUnterhaltungId, message.timestamp.ifBlank { message.datum },
                    message.linkedinNachrichtAutor, message.linkedinNachrichtEigen.toString(),
                    message.bemerkung, message.linkedinNachrichtBetreff, message.linkedinNachrichtAnhaenge)
                    .joinToString("") { "${it.length}:$it" }
            message.copy(id = identity)
        }.distinctBy { it.id }
    suspend fun lead(id: String): All = json.decodeFromString(All.serializer(), read("lead", id))
    suspend fun settings(): All = json.decodeFromString(All.serializer(), read("settings", "")).also { applyCheckpointLabels(it.vertriebCheckpointLabels) }
    suspend fun chat(id: String? = null): All {
        val data = json.decodeFromString(All.serializer(), read("chat", id.orEmpty()))
        applyCheckpointLabels(data.vertriebCheckpointLabels)
        return data.copy(linkedinKontakte = distinctLinkedInContacts(data.linkedinKontakte),
            linkedinNachrichten = data.linkedinNachrichten.distinctBy { it.id })
    }
    suspend fun contactStatus(): List<All> = json.decodeFromString(ListSerializer(All.serializer()), read("contact-status", ""))
    suspend fun overview(): All = json.decodeFromString(All.serializer(), read("lead-overview", ""))
    suspend fun recordConversation(id: String, kind: String, text: String, eventId: String): All = post("conversation", buildJsonObject {
        put("id", id); put("kind", kind); put("text", text); put("eventId", eventId)
    }.toString())
    suspend fun saveFollowUpDays(days: Int): All {
        val response = window.fetch("${LumaticaApiSettings.getBaseUrl()}/api/linkedinflow/settings-save", RequestInit(
            method = "POST", headers = LumaticaApiManager.auditHeaders("application/json"),
            body = json.encodeToString(All.serializer(), All(linkedinFollowUpTage = days))
        )).await()
        val raw = response.text().await()
        if (!response.ok) error("Follow-up-Frist konnte nicht gespeichert werden (HTTP ${response.status}).")
        return json.decodeFromString(All.serializer(), raw)
    }
    private suspend fun post(action: String, body: String): All {
        val response = window.fetch("${LumaticaApiSettings.getBaseUrl()}/api/linkedinflow/$action", RequestInit(
            method = "POST", headers = LumaticaApiManager.auditHeaders("application/json"), body = body)).await()
        val raw = response.text().await()
        if (!response.ok) error(runCatching { json.parseToJsonElement(raw).jsonObject["message"]?.jsonPrimitive?.contentOrNull }.getOrNull()
            ?: "Aktion konnte nicht gespeichert werden (HTTP ${response.status}).")
        return json.decodeFromString(All.serializer(), raw)
    }
    suspend fun syncStatus(): All = json.decodeFromString(All.serializer(), read("sync-status", ""))
    suspend fun synchronize(scope: String = "all"): All {
        check(!LinkedInSyncState.busy) { "Eine Synchronisation läuft bereits." }
        LinkedInSyncState.busy = true
        try {
            val result = post("sync", buildJsonObject { put("scope", scope) }.toString())
            LinkedInSyncState.status = result
            LinkedInSyncState.version++
            return result
        } finally { LinkedInSyncState.busy = false }
    }
    suspend fun checkpoint(id: String, action: String, confirmed: Boolean = false, bookingId: String = "",
        note: String = "", result: String = "", reminder: String = ""): All = post("checkpoint", buildJsonObject {
        put("id", id); put("action", action); put("confirmed", confirmed); put("bookingId", bookingId)
        put("note", note); put("result", result); put("reminder", reminder)
    }.toString())
    suspend fun saveCheckpointDeadlines(values: Map<String, Int>): All = post("settings-save", buildJsonObject {
        put("vertriebCheckpointFristen", buildJsonObject { values.forEach { (key, days) -> put(key, days) } })
    }.toString())
    suspend fun saveCheckpointLabels(values: Map<String, String>): All = post("settings-save", buildJsonObject {
        put("vertriebCheckpointLabels", buildJsonObject { values.forEach { (key, label) -> put(key, label) } })
    }.toString()).also { applyCheckpointLabels(it.vertriebCheckpointLabels) }
    suspend fun contacts(): List<All> {
        return distinctLinkedInContacts(json.decodeFromString(ListSerializer(All.serializer()), read("contacts", "")))
    }
    suspend fun save(contactId: String, lead: All, action: String = "save"): All {
        require(action in setOf("save", "material", "finish", "hide"))
        val response = window.fetch("${LumaticaApiSettings.getBaseUrl()}/api/linkedinflow/$action", RequestInit(
            method = "POST", headers = LumaticaApiManager.auditHeaders("application/json"),
            body = json.encodeToString(All.serializer(), lead.copy(id = contactId))
        )).await()
        val raw = response.text().await()
        if (!response.ok) error("DM Flow konnte nicht gespeichert werden (HTTP ${response.status}).")
        return json.decodeFromString(All.serializer(), raw)
    }
}
